import React, { useState } from "react";
import {
  ArrowRight,
  Brain,
  Shield,
  Zap,
  Heart,
  Calendar,
  TrendingUp,
  Lock,
  Bell,
  Eye,
  Activity,
  Smartphone,
} from "lucide-react";

const HealthAIPage = () => {
  const [email, setEmail] = useState("");

  const handleEarlyAccess = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle early access signup
    console.log("Early access signup:", email);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-midnight-blue via-midnight-blue/95 to-electric-blue/20 text-white">
      {/* Hero Section */}
      <section className="px-4 py-16 md:py-24 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-electric-blue/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-burnt-orange/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            {/* Coming Soon Badge */}
            <div className="flex justify-center mb-8">
              <div className="glass-dark p-4 rounded-2xl inline-flex items-center gap-3 animate-neural-pulse">
                <div className="w-12 h-12 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-xl flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-xs font-medium text-electric-blue uppercase tracking-wider">
                    Sắp Ra Mắt Q2/2026
                  </div>
                  <div className="text-lg font-bold text-white">
                    Health AI Profile
                  </div>
                </div>
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Tương Lai Của
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-blue to-burnt-orange relative">
                Chăm Sóc Sức Khỏe Gia Đình
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-electric-blue to-burnt-orange rounded-full"></div>
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-4xl mx-auto leading-relaxed">
              Locuno Health AI sẽ thay đổi cách bạn theo dõi và chăm sóc sức
              khỏe gia đình. Từ điện thoại thông minh đến thông tin sức khỏe
              thông minh - tất cả trong một ứng dụng.
            </p>

            {/* Key Features Preview */}
            <div className="flex flex-wrap justify-center items-center gap-8 mb-12 text-white/70">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-electric-blue" />
                <span className="text-sm font-medium">
                  AI Phân tích sức khỏe
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium">Bảo mật tuyệt đối</span>
              </div>
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-burnt-orange" />
                <span className="text-sm font-medium">Chỉ cần điện thoại</span>
              </div>
            </div>

            {/* Early Access Form */}
            <div className="max-w-md mx-auto mb-16">
              <form
                onSubmit={handleEarlyAccess}
                className="glass-dark p-6 rounded-2xl"
              >
                <h3 className="text-xl font-bold mb-4 text-white">
                  Đăng Ký Trải Nghiệm Sớm
                </h3>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email của bạn"
                    className="input-glass flex-1"
                    required
                  />
                  <button type="submit" className="btn-primary">
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-white/60 mt-3">
                  ✓ Miễn phí cho 100 gia đình đầu tiên ✓ Thông báo khi ra mắt
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features Preview */}
      <section className="px-4 py-16 bg-gradient-to-r from-electric-blue/10 to-burnt-orange/10">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Trí Tuệ Nhân Tạo Cho Sức Khỏe Gia Đình
            </h2>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              Công nghệ AI tiên tiến sẽ biến điện thoại thành trợ lý sức khỏe cá
              nhân, theo dõi và cảnh báo sớm các vấn đề sức khỏe cho cả gia
              đình.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1: Health Monitoring */}
            <div className="glass-dark p-6 rounded-2xl hover:bg-electric-blue/20 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-electric-blue/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">
                Theo Dõi Sức Khỏe Thông Minh
              </h3>
              <p className="text-white/80 mb-3 leading-relaxed">
                AI phân tích các dấu hiệu từ điện thoại: nhịp tim, giấc ngủ,
                hoạt động hàng ngày. Phát hiện sớm các bất thường và đưa ra lời
                khuyên phù hợp.
              </p>
              <div className="text-sm text-electric-blue font-medium bg-electric-blue/10 px-3 py-1 rounded-full inline-block">
                Độ chính xác 94%
              </div>
            </div>

            {/* Feature 2: Family Health Dashboard */}
            <div className="glass-dark p-6 rounded-2xl hover:bg-burnt-orange/20 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange to-burnt-orange/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">
                Bảng Điều Khiển Gia Đình
              </h3>
              <p className="text-white/80 mb-3 leading-relaxed">
                Một cái nhìn tổng quan về sức khỏe cả gia đình. Theo dõi xu
                hướng, so sánh tiến triển và nhận cảnh báo khi cần thiết.
              </p>
              <div className="text-sm text-burnt-orange font-medium bg-burnt-orange/10 px-3 py-1 rounded-full inline-block">
                Real-time updates
              </div>
            </div>

            {/* Feature 3: AI Health Assistant */}
            <div className="glass-dark p-6 rounded-2xl hover:bg-electric-blue/20 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue/80 to-burnt-orange rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">
                Trợ Lý AI Cá Nhân
              </h3>
              <p className="text-white/80 mb-3 leading-relaxed">
                Chat với AI để được tư vấn sức khỏe 24/7. Hỏi đáp về triệu
                chứng, nhận lời khuyên về lối sống và được nhắc nhở khám định
                kỳ.
              </p>
              <div className="text-sm text-electric-blue font-medium bg-electric-blue/10 px-3 py-1 rounded-full inline-block">
                24/7 Available
              </div>
            </div>

            {/* Feature 4: Emergency Detection */}
            <div className="glass-dark p-6 rounded-2xl hover:bg-burnt-orange/20 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange/80 to-electric-blue rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">
                Phát Hiện Khẩn Cấp
              </h3>
              <p className="text-white/80 mb-3 leading-relaxed">
                AI tự động phát hiện các tình huống khẩn cấp: ngã, bất tỉnh,
                nhịp tim bất thường và gửi cảnh báo cho người thân ngay lập tức.
              </p>
              <div className="text-sm text-burnt-orange font-medium bg-burnt-orange/10 px-3 py-1 rounded-full inline-block">
                Auto-detection
              </div>
            </div>

            {/* Feature 5: Preventive Insights */}
            <div className="glass-dark p-6 rounded-2xl hover:bg-electric-blue/20 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-electric-blue to-burnt-orange/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">
                Dự Đoán & Phòng Ngừa
              </h3>
              <p className="text-white/80 mb-3 leading-relaxed">
                Phân tích dữ liệu dài hạn để dự đoán nguy cơ bệnh tật. Đưa ra kế
                hoạch phòng ngừa cá nhân hóa cho từng thành viên.
              </p>
              <div className="text-sm text-electric-blue font-medium bg-electric-blue/10 px-3 py-1 rounded-full inline-block">
                Predictive AI
              </div>
            </div>

            {/* Feature 6: Privacy First */}
            <div className="glass-dark p-6 rounded-2xl hover:bg-burnt-orange/20 transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange to-electric-blue/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">
                Quyền Riêng Tư Tuyệt Đối
              </h3>
              <p className="text-white/80 mb-3 leading-relaxed">
                Tất cả dữ liệu sức khỏe được mã hóa và lưu trữ cục bộ. Bạn hoàn
                toàn kiểm soát thông tin của mình.
              </p>
              <div className="text-sm text-burnt-orange font-medium bg-burnt-orange/10 px-3 py-1 rounded-full inline-block">
                On-device AI
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Timeline */}
      <section className="px-4 py-16">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Lộ Trình Phát Triển
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Từ ý tưởng đến hiện thực - cùng theo dõi hành trình phát triển
              Health AI Profile.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-electric-blue to-burnt-orange rounded-full"></div>

            {/* Timeline Items */}
            <div className="space-y-12">
              {/* Q4 2025 - Research */}
              <div className="flex items-center">
                <div className="w-1/2 pr-8 text-right">
                  <div className="glass-dark p-6 rounded-2xl">
                    <div className="text-sm text-electric-blue font-medium mb-2">
                      Q4 2025
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">
                      Nghiên Cứu & Phát Triển
                    </h3>
                    <p className="text-white/70">
                      Hoàn thiện thuật toán AI, thử nghiệm với dữ liệu sức khỏe
                      cơ bản từ điện thoại.
                    </p>
                  </div>
                </div>
                <div className="w-8 h-8 bg-electric-blue rounded-full border-4 border-midnight-blue flex items-center justify-center z-10">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <div className="w-1/2 pl-8"></div>
              </div>

              {/* Q1 2026 - Beta Testing */}
              <div className="flex items-center">
                <div className="w-1/2 pr-8"></div>
                <div className="w-8 h-8 bg-burnt-orange rounded-full border-4 border-midnight-blue flex items-center justify-center z-10">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div className="w-1/2 pl-8">
                  <div className="glass-dark p-6 rounded-2xl">
                    <div className="text-sm text-burnt-orange font-medium mb-2">
                      Q1 2026
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">
                      Beta Testing
                    </h3>
                    <p className="text-white/70">
                      Thử nghiệm với 100 gia đình tiên phong, thu thập phản hồi
                      và cải thiện tính năng.
                    </p>
                  </div>
                </div>
              </div>

              {/* Q2 2026 - Launch */}
              <div className="flex items-center">
                <div className="w-1/2 pr-8 text-right">
                  <div className="glass-primary p-6 rounded-2xl border-2 border-electric-blue animate-pulse">
                    <div className="text-sm text-electric-blue font-medium mb-2">
                      Q2 2026
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">
                      Ra Mắt Chính Thức
                    </h3>
                    <p className="text-white/70">
                      Health AI Profile chính thức ra mắt với đầy đủ tính năng
                      cho toàn bộ cộng đồng Locuno.
                    </p>
                  </div>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full border-4 border-midnight-blue flex items-center justify-center z-10 animate-pulse">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div className="w-1/2 pl-8"></div>
              </div>

              {/* Beyond 2026 - Evolution */}
              <div className="flex items-center">
                <div className="w-1/2 pr-8"></div>
                <div className="w-8 h-8 bg-white/20 rounded-full border-4 border-midnight-blue flex items-center justify-center z-10">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <div className="w-1/2 pl-8">
                  <div className="glass-dark p-6 rounded-2xl">
                    <div className="text-sm text-white/60 font-medium mb-2">
                      Beyond 2026
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">
                      Tiến Hóa Không Ngừng
                    </h3>
                    <p className="text-white/70">
                      Tích hợp thiết bị đeo, AI tiến hóa, mở rộng sang chăm sóc
                      sức khỏe toàn diện.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="px-4 py-16 bg-gradient-to-r from-electric-blue/5 to-burnt-orange/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Công Nghệ Tiên Tiến
            </h2>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              Locuno Health AI được xây dựng trên nền tảng công nghệ hàng đầu
              thế giới, đảm bảo độ chính xác và bảo mật tuyệt đối.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              "Machine Learning",
              "Neural Networks",
              "Edge Computing",
              "Blockchain Security",
            ].map((tech, index) => (
              <div
                key={tech}
                className="glass-dark p-6 rounded-xl text-center group hover:bg-electric-blue/10 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-electric-blue to-burnt-orange rounded-full mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold">{index + 1}</span>
                </div>
                <h3 className="font-semibold text-white text-sm">{tech}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-16">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Hãy Là Người Đầu Tiên Trải Nghiệm Tương Lai
          </h2>
          <p className="text-lg text-white/80 mb-12 max-w-2xl mx-auto">
            Đăng ký ngay để nhận thông báo khi Health AI Profile ra mắt và được
            ưu tiên trải nghiệm miễn phí trong 6 tháng đầu.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <button className="btn-primary group px-8 py-4">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5" />
                <span>Thông Báo Khi Ra Mắt</span>
                <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
            <button className="btn-secondary px-8 py-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5" />
                <span>Đặt Lịch Demo</span>
              </div>
            </button>
          </div>

          {/* Trust & Privacy */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-400" />
              <span>ISO 27001 Certified</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-electric-blue" />
              <span>End-to-End Encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-burnt-orange" />
              <span>100% Privacy Guaranteed</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthAIPage;
